import { useEffect, useRef, useState } from 'react';
import { immichOriginalUrl, immichThumbnailUrl } from '@/api/immich';
import { type ClassName, twx } from '@/utils/tailwind';

export const immichImageSizes = ['thumbnail', 'preview', 'original'] as const;
export type ImmichImageSize = (typeof immichImageSizes)[number];

export interface ImmichImageProps {
  /** Immich asset ID — opaque. */
  assetId: string;
  /** Which Immich rendition to request. Defaults to `preview` (the
   *  larger thumbnail variant — good for itinerary cards). Use
   *  `original` for fullscreen viewers. */
  size?: ImmichImageSize;
  alt: string;
  className?: ClassName;
  /** Fires once the image successfully loads, after any necessary retries. */
  onLoaded?: () => void;
  /** Fires after the final retry attempt fails. */
  onFailed?: () => void;
}

/** Number of times we'll retry a failing fetch before giving up. Five
 *  retries with the backoff below works out to ~22s of total patience —
 *  comfortably longer than Immich's typical thumbnail-generation lag. */
const MAX_RETRIES = 5;
/** Backoff schedule, milliseconds. Index = retry attempt number. After
 *  retry 5 we give up and surface the failed state. */
const BACKOFF_MS = [800, 1500, 2500, 4000, 6000, 8000];

const urlFor = (assetId: string, size: ImmichImageSize): string =>
  size === 'original' ? immichOriginalUrl(assetId) : immichThumbnailUrl(assetId, size);

/**
 * Worker-proxied Immich image with auto-retry on transient failures.
 *
 * Immich generates thumbnails asynchronously after upload — the first
 * GET against a fresh asset can return 502 (via our worker) while the
 * background thumbnail job runs. This component swallows those failures
 * and retries with backoff, so a newly-uploaded photo doesn't render as
 * a broken icon until the user reloads.
 *
 * While loading (and between retries), a shimmer block covers the
 * `<img>` so the layout stays stable; the shimmer fades out when the
 * image finally loads.
 */
export const ImmichImage = ({
  assetId,
  size = 'preview',
  alt,
  className,
  onLoaded,
  onFailed,
}: ImmichImageProps) => {
  const [attempt, setAttempt] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset state when the assetId changes (e.g. parent swaps photos in
  // the lightbox). Without this, switching between assets would start
  // mid-retry on the new asset.
  useEffect(() => {
    setAttempt(0);
    setLoaded(false);
    setFailed(false);
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  }, []);

  // Cleanup any in-flight retry timer on unmount so we don't setState
  // against a torn-down component.
  useEffect(
    () => () => {
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    },
    [],
  );

  // `?_a=N` cache-busts the failed response so the browser actually
  // re-fetches instead of reusing a cached 502. The base URL stays
  // stable on success so the long worker cache header still kicks in.
  const src = `${urlFor(assetId, size)}${attempt > 0 ? `&_a=${attempt}` : ''}`;

  const handleError = () => {
    if (failed) return;
    if (attempt >= MAX_RETRIES) {
      setFailed(true);
      onFailed?.();
      return;
    }
    const delay = BACKOFF_MS[attempt] ?? BACKOFF_MS[BACKOFF_MS.length - 1] ?? 8000;
    retryTimerRef.current = setTimeout(() => setAttempt((a) => a + 1), delay);
  };

  const handleLoad = () => {
    setLoaded(true);
    setFailed(false);
    onLoaded?.();
  };

  return (
    <>
      {!(loaded || failed) && (
        <span
          aria-hidden
          className={twx('absolute inset-0 bg-kawaii-shimmer motion-safe:animate-kawaii-shimmer')}
        />
      )}
      <img
        // The key forces React to re-mount the <img> on each retry — some
        // browsers don't re-fetch when only the src attribute mutates.
        key={attempt}
        src={src}
        alt={alt}
        loading="lazy"
        onLoad={handleLoad}
        onError={handleError}
        className={twx(
          'transition-opacity duration-300',
          loaded ? 'opacity-100' : 'opacity-0',
          failed && 'opacity-100',
          className,
        )}
      />
    </>
  );
};
